import sortBy from 'lodash/sortBy'
import {fromJust} from '@freckle/maybe'
import type {TeacherRole} from '@freckle/educator-entities/ts/common/helpers/teacher-role'
import {TeacherRoles} from '@freckle/educator-entities/ts/common/helpers/teacher-role'
import type {IdentityProviderT} from '@freckle/educator-entities/ts/roster/identity-provider'
import {getIdpLabel} from '@freckle/educator-entities/ts/roster/identity-provider'
import {formattedName} from '@freckle/educator-entities/ts/users/models/teacher'
import {Link} from '@freckle/educator-materials/ts/components/link'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {
  Routes,
  TEACHER_FILTER_QUERY_PARAM_TAG,
  STUDENT_FILTER_QUERY_PARAM_TAG,
  COURSE_FILTER_QUERY_PARAM_TAG
} from '@freckle/school/ts/common/routers/routes'
import type {TabKeyT} from '@freckle/school/ts/manage-teachers/components/manage-teachers/utils'
import type {
  TeacherSimplifiedAttrT,
  StudentSimplifiedT
} from '@freckle/school/ts/manage-teachers/models/course-table-row'
import {missingField} from './cell.module.scss'

export const Grades = ({grades}: {grades: Array<number>}) => {
  const {t} = useSafeTranslation()
  return (
    <>
      {sortBy(grades)
        .map(grade => t('DISPLAY_GRADE', {grade}))
        .join(', ')}
    </>
  )
}

export const Role = ({role}: {role: TeacherRole | null | undefined}) => {
  const {t} = useSafeTranslation()
  if (role === null || role === undefined) {
    return <>{t('MANAGE_TEACHERS_TEACHER_TAB_TEACHER_TABLE_UNKNOWN_ROLE')}</>
  }

  return <>{TeacherRoles.forDisplay(role)}</>
}

export const LinkedTeacher = ({
  row: {teacher, id, name}
}: {
  row: {
    id: number
    name: string
    teacher: TeacherSimplifiedAttrT
  }
}) => {
  const queryParams = {
    activeTab: 'teacher-tab' as TabKeyT,
    additionalFilters: {
      tag: TEACHER_FILTER_QUERY_PARAM_TAG,
      courseId: id,
      courseName: name
    }
  }
  return (
    <Link reactRouter href={Routes.dashboard(queryParams)}>
      {`${teacher.firstName} ${teacher.lastName}`}
    </Link>
  )
}

export const LinkedTeacherCount = ({
  row: {teachers, id, name}
}: {
  row: {teachers: {firstName: string; lastName: string}[]; id: number; name: string}
}) => {
  const columnText = teacherColumnText(teachers)
  const queryParams = {
    activeTab: 'teacher-tab' as const,
    additionalFilters: {
      tag: TEACHER_FILTER_QUERY_PARAM_TAG,
      studentId: id,
      studentName: name
    }
  }
  return (
    <Link reactRouter href={Routes.dashboard(queryParams)}>
      {columnText}
    </Link>
  )
}

function teacherColumnText(teachers: {firstName: string; lastName: string}[]): string {
  if (teachers.length === 1) {
    const teacher = fromJust(teachers[0], `Impossible`)
    return formattedName(teacher)
  }
  return teachers.length.toString()
}

export const LinkedStudentCount = ({
  row: {id, name, students},
  currentSchoolIds
}: {
  row: {id: number; name: string; students: StudentSimplifiedT[]}
  currentSchoolIds: Array<number> | null | undefined
}) => {
  const queryParams = {
    schoolIds: currentSchoolIds,
    activeTab: 'student-tab' as TabKeyT,
    additionalFilters: {
      tag: STUDENT_FILTER_QUERY_PARAM_TAG,
      courseId: id,
      courseName: name
    }
  }
  return (
    <Link reactRouter href={Routes.dashboard(queryParams)}>
      {students.length}
    </Link>
  )
}

export const LinkedGrade = ({
  grade,
  currentSchoolIds
}: {
  grade: number
  currentSchoolIds: Array<number> | null | undefined
}) => {
  const {t} = useSafeTranslation()
  const queryParams = {
    schoolIds: currentSchoolIds,
    activeTab: 'student-tab' as const,
    additionalFilters: {
      tag: STUDENT_FILTER_QUERY_PARAM_TAG,
      grade
    }
  }
  return (
    <Link reactRouter href={Routes.dashboard(queryParams)}>
      {t('DISPLAY_GRADE', {grade})}
    </Link>
  )
}

export const LinkedCourseCount = ({
  row: {id, name, coursesCount}
}: {
  row: {id: number; name: string; coursesCount: number}
}) => {
  const queryParams = {
    activeTab: 'course-tab' as const,
    additionalFilters: {
      tag: COURSE_FILTER_QUERY_PARAM_TAG,
      studentId: id,
      studentName: name
    }
  }
  return (
    <Link reactRouter href={Routes.dashboard(queryParams)}>
      {coursesCount}
    </Link>
  )
}

export const Idp = ({source}: {source: IdentityProviderT | null | undefined}) => {
  const {t} = useSafeTranslation()
  return <>{getIdpLabel(t, source)}</>
}

export const SisId = ({sisId}: {sisId: string | null | undefined}) => {
  const {t} = useSafeTranslation()
  if (sisId !== null && sisId !== undefined) {
    return <>{sisId.toString()}</>
  }
  return (
    <span className={missingField}>
      {t('MANAGE_TEACHERS_STUDENT_TAB_STUDENT_TABLE_MISSING_FIELD')}
    </span>
  )
}
